import { HttpApiClient } from './httpApiClient';
import { WsApiClient } from './wsApiClient';
import { SearchResult, AutocompleteResponse, SearchParams } from './types';

export class ApiClient {
  private httpApiClient: HttpApiClient;
  private wsApiClient: WsApiClient | null = null;
  private readonly useWebSocket: boolean;

  constructor(
    apiKey: string,
    clientId: string,
    baseUrl: string = '',
    useWebSocket: boolean = true
  ) {
    this.httpApiClient = new HttpApiClient(apiKey, clientId, baseUrl);
    this.useWebSocket = useWebSocket;

    if (useWebSocket) {
      const wsBaseUrl = baseUrl
        ? baseUrl.replace(/^http/, 'ws') + '/' + clientId + '/ws'
        : 'ws://localhost:8080/ws';
      this.wsApiClient = new WsApiClient(wsBaseUrl, apiKey, this.httpApiClient);
    }
  }

  async fetchAutocomplete(query: string): Promise<AutocompleteResponse> {
    // Use WS if enabled and connected, otherwise HTTP
    if (this.useWebSocket && this.wsApiClient?.isConnected()) {
      try {
        return await this.wsApiClient.autocomplete(query);
      } catch (e) {
        // WS call failed (connection dropped mid-request, timeout, etc.)
        // Fall back to HTTP for this request
        console.warn(
          '[ApiClient] WS autocomplete failed, using HTTP fallback',
          e
        );
        return this.httpApiClient.fetchAutocomplete(query);
      }
    }
    return this.httpApiClient.fetchAutocomplete(query);
  }

  async search(searchParams: SearchParams): Promise<SearchResult> {
    // Use WS if enabled and connected, otherwise HTTP
    if (this.useWebSocket && this.wsApiClient?.isConnected()) {
      try {
        return await this.wsApiClient.search(searchParams);
      } catch (e) {
        // WS call failed (connection dropped mid-request, timeout, etc.)
        // Fall back to HTTP for this request
        console.warn('[ApiClient] WS search failed, using HTTP fallback', e);
        return this.httpApiClient.search(searchParams);
      }
    }
    return this.httpApiClient.search(searchParams);
  }
}
